import LinearProgress from "@mui/material/LinearProgress";
import { LoaderCircle } from "lucide-react";

// BRK-S460 (Nav, 2026-10-26: "we should display loading screen, like generic
// one when we are pulling the quotes"): while any carrier's quote is being
// fetched, the quotes wait behind this, so an earlier or outdated quote and
// its way to ask again never read as the answer meanwhile. A progress bar,
// each carrier being fetched, and placeholder cards in the cards' shape, as
// a step's first load shows (StepLoading, BRK-S368). Under reduced motion the
// bar goes and nothing spins or pulses.

const PLACEHOLDER = "block bg-brk-bg motion-safe:animate-pulse";

export function QuotesLoading({
  carriers,
  placeholders,
}: {
  /** The carriers whose quotes are being fetched, in card order. */
  carriers: readonly string[];
  /** How many cards to hold a place for: at least one, at most a row. */
  placeholders: number;
}) {
  return (
    <div
      aria-busy="true"
      data-quotes-loading
      className="flex flex-col gap-4 rounded-lg border border-brk-border bg-brk-panel p-4"
    >
      <div className="flex flex-col gap-1">
        <p role="status" className="text-body font-medium text-brk-text">
          Getting your quotes…
        </p>
        <p className="text-sm text-brk-dim">
          This can take a minute or two. They show here once they’re in.
        </p>
      </div>
      <LinearProgress
        aria-hidden="true"
        className="motion-reduce:hidden"
        sx={{ borderRadius: 1 }}
      />
      <ul aria-label="Quotes on their way" className="flex flex-col gap-2">
        {carriers.map((carrier, i) => (
          <li
            key={`${i}-${carrier}`}
            className="flex items-center gap-2 text-body text-brk-text"
          >
            <LoaderCircle
              aria-hidden="true"
              className="h-4 w-4 shrink-0 text-brk-accent motion-safe:animate-spin"
            />
            {`Getting ${carrier}’s quote…`}
          </li>
        ))}
      </ul>
      <div
        aria-hidden="true"
        className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
      >
        {Array.from(
          { length: Math.min(Math.max(placeholders, 1), 3) },
          (_, i) => (
            <span
              key={i}
              data-quotes-loading-card
              className="flex flex-col gap-3 rounded-lg border border-brk-border p-4"
            >
              <span className={`${PLACEHOLDER} h-4 w-1/2 rounded`} />
              <span className={`${PLACEHOLDER} h-8 w-2/3 rounded`} />
              <span className={`${PLACEHOLDER} h-3 w-full rounded`} />
              <span className={`${PLACEHOLDER} h-3 w-5/6 rounded`} />
            </span>
          ),
        )}
      </div>
    </div>
  );
}
